/**
 * BlacklistWarning — reusable inline warning banner shown in CustomerForm
 * and LoanDialog when CNIC/Mobile match a blacklist entry (own-shop or
 * network-shared). Calls /api/blacklist/check on demand.
 */
import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Ban, AlertTriangle, Network, Store } from "lucide-react";

export default function BlacklistWarning({ cnic, mobile, onResult }) {
  const [state, setState] = useState({ matches: [], mode: "warn", loading: false });

  useEffect(() => {
    let active = true;
    const c = (cnic || "").trim();
    const m = (mobile || "").trim();
    if (!c && !m) {
      // Schedule the reset so it doesn't fire during render commit
      const reset = setTimeout(() => {
        if (!active) return;
        setState({ matches: [], mode: "warn", loading: false });
        if (onResult) onResult({ matches: [], mode: "warn" });
      }, 0);
      return () => { active = false; clearTimeout(reset); };
    }
    const t = setTimeout(async () => {
      setState((s) => ({ ...s, loading: true }));
      try {
        const params = {};
        if (c) params.cnic = c;
        if (m) params.mobile = m;
        const { data } = await api.get("/blacklist/check", { params });
        if (!active) return;
        const next = { matches: data.matches || [], mode: data.enforcement_mode || "warn", loading: false };
        setState(next);
        if (onResult) onResult(next);
      } catch {
        if (active) setState({ matches: [], mode: "warn", loading: false });
      }
    }, 350);
    return () => { active = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cnic, mobile]);

  if (!state.matches.length) return null;
  const isBlock = state.mode === "block";
  const tone = isBlock
    ? "bg-red-50 border-red-300 text-red-800"
    : "bg-amber-50 border-amber-300 text-amber-800";
  const Icon = isBlock ? Ban : AlertTriangle;

  return (
    <div
      className={`border rounded-md p-3 ${tone}`}
      data-testid="blacklist-warning"
      data-mode={state.mode}
    >
      <div className="flex items-center gap-2 font-display font-bold text-sm">
        <Icon className="w-4 h-4" />
        {isBlock
          ? "Blacklisted — Save action will be blocked"
          : "Blacklist match mila — savdhani se aagey barhein"}
      </div>
      <div className="text-xs mt-1.5 space-y-1">
        {state.matches.slice(0, 5).map((m) => (
          <div
            key={m.id}
            className="flex items-start gap-2"
            data-testid={`blacklist-match-${m.id}`}
          >
            {m.scope === "network" ? (
              <Network className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            ) : (
              <Store className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            )}
            <div>
              <span className="font-semibold">{m.name}</span>
              {m.cnic && <span className="opacity-70"> · CNIC {m.cnic}</span>}
              {m.mobile && <span className="opacity-70"> · {m.mobile}</span>}
              <span className="ml-1 uppercase text-[10px] font-bold opacity-70">
                ({m.scope === "network" ? "Network" : "Aap ki shop"} · {m.severity})
              </span>
              <div className="opacity-80">Reason: {m.reason}</div>
            </div>
          </div>
        ))}
        {state.matches.length > 5 && (
          <div className="opacity-70">+ {state.matches.length - 5} aur entries</div>
        )}
      </div>
    </div>
  );
}
